/**
 * Nested folder + file tree for Scripts Inventory.
 * Folders are navigational only (any depth); files are the selectable leaves.
 */

import type { TestScriptFileRow } from '@/components/smartai/api/getTestScriptFiles';

export type FolderAccent = 'p1' | 'p2' | 'p3' | 'neutral';

export interface ScriptFileNode {
  type: 'file';
  /** Display name (basename). */
  segment: string;
  /** Full inventory filePath (selection key). */
  pathKey: string;
  filePath: string;
  fileName: string;
}

export interface ScriptFolderNode {
  type: 'folder';
  segment: string;
  pathKey: string;
  accent: FolderAccent;
  /** All script files strictly under this folder (recursive). */
  descendantScriptCount: number;
  children: ScriptTreeNode[];
}

export type ScriptTreeNode = ScriptFolderNode | ScriptFileNode;

/**
 * Map a path under `Scripts/` to `testSessions.testCaseName`.
 * Replaces spaces and `/` with `_` in each segment, then joins with `_`.
 */
export function testCaseNameFromScriptsPath(pathUnderScripts: string): string {
  const normalized = String(pathUnderScripts ?? '')
    .trim()
    .replace(/\\/g, '/')
    .replace(/^\/+|\/+$/g, '');
  if (!normalized) return '';

  return normalized
    .split('/')
    .filter(Boolean)
    .map(segment => segment.replace(/ /g, '_').replace(/\//g, '_'))
    .join('_');
}

/**
 * Coverage lookup key for a selected inventory file.
 * - Katalon `.groovy`: parent folder path → underscore testCaseName
 * - Other scripts: file stem (strip `.spec.ts` / `.ts`, etc.) so regex match
 *   finds sessions like `tests_bug_report_jira_spec_test_frontend` or
 *   `_unstructured_start_project_user_story-spec_test_frontend`
 */
export function testCaseNameFromInventoryFilePath(filePath: string): string {
  const segs = segmentsUnderScriptsRoot(filePath);
  if (!segs.length) return '';

  const last = segs[segs.length - 1] ?? '';

  if (/\.groovy$/i.test(last)) {
    const folderSegs = segs.slice(0, -1);
    return testCaseNameFromScriptsPath(folderSegs.join('/'));
  }

  return last
    .replace(/\.(spec|test)\.(ts|tsx|js|jsx|mjs|cjs)$/i, '')
    .replace(/\.(ts|tsx|js|jsx|mjs|cjs)$/i, '')
    .replace(/[\s./\\]+/g, '_');
}

/** @deprecated Prefer selecting a file; kept for any folder-key callers. */
export function testCaseNameFromFolderPathKey(pathKey: string): string {
  return testCaseNameFromScriptsPath(pathKey);
}

/** Segments after the first case-insensitive `Scripts` segment (or full path). */
export function segmentsUnderScriptsRoot(filePath: string): string[] {
  const raw = String(filePath ?? '')
    .trim()
    .replace(/\\/g, '/');
  const parts = raw.split('/').filter(s => s.length > 0);
  const scriptsIdx = parts.findIndex(p => /^scripts$/i.test(p));
  if (scriptsIdx === -1) return parts.slice();
  return parts.slice(scriptsIdx + 1);
}

export function inferFolderAccent(segment: string): FolderAccent {
  const s = segment.toLowerCase();
  if (/priority\s*[:,]?\s*1|^p\s*1(?:\s|$|-|_|\/)|^_?p\s*1_/.test(s)) return 'p1';
  if (/priority\s*[:,]?\s*2|^p\s*2(?:\s|$|-|_|\/)|^_?p\s*2_/.test(s)) return 'p2';
  if (/priority\s*[:,]?\s*3|^p\s*3(?:\s|$|-|_|\/)|^_?p\s*3_/.test(s)) return 'p3';
  if (/\bpriority\b/.test(s)) return 'p2';
  if (/\bbatch\b/.test(s)) return 'neutral';
  return 'neutral';
}

type MutableFolder = {
  segment: string;
  pathKey: string;
  dirs: Map<string, MutableFolder>;
  files: ScriptFileNode[];
};

function mutableToFolderNode(m: MutableFolder): ScriptFolderNode {
  const childFolders = [...m.dirs.values()].sort((a, b) =>
    a.segment.localeCompare(b.segment, undefined, { sensitivity: 'base' }),
  );
  const childFiles = [...m.files].sort((a, b) =>
    a.segment.localeCompare(b.segment, undefined, { sensitivity: 'base' }),
  );

  const children: ScriptTreeNode[] = [
    ...childFolders.map(mutableToFolderNode),
    ...childFiles,
  ];

  let descendantScriptCount = 0;
  for (const ch of children) {
    if (ch.type === 'file') descendantScriptCount += 1;
    else descendantScriptCount += ch.descendantScriptCount;
  }

  return {
    type: 'folder',
    segment: m.segment,
    pathKey: m.pathKey,
    accent: inferFolderAccent(m.segment),
    descendantScriptCount,
    children,
  };
}

/**
 * Nested folders (any depth) + file leaves from inventory rows.
 * Does not expose a synthetic `Scripts` root — returns its immediate children.
 */
export function buildScriptFilesTree(
  files: TestScriptFileRow[],
): ScriptTreeNode[] {
  const hidden: MutableFolder = {
    segment: '',
    pathKey: '',
    dirs: new Map(),
    files: [],
  };

  const seenPath = new Set<string>();

  for (const meta of files) {
    const filePath = String(meta.filePath ?? '').trim();
    if (!filePath) continue;

    const segs = segmentsUnderScriptsRoot(filePath);
    if (!segs.length) continue;

    const dedupe = `${filePath}|${String(meta.contentHash ?? '')}`;
    if (seenPath.has(dedupe)) continue;
    seenPath.add(dedupe);

    const fileName = segs[segs.length - 1] ?? meta.fileName ?? filePath;
    const folderSegs = segs.slice(0, -1);
    const fileNode: ScriptFileNode = {
      type: 'file',
      segment: fileName,
      pathKey: filePath,
      filePath,
      fileName: String(meta.fileName ?? fileName),
    };

    if (folderSegs.length === 0) {
      hidden.files.push(fileNode);
      continue;
    }

    let cur = hidden;
    for (const segment of folderSegs) {
      const nextPath =
        cur.pathKey === '' ? segment : `${cur.pathKey}/${segment}`;
      if (!cur.dirs.has(segment)) {
        cur.dirs.set(segment, {
          segment,
          pathKey: nextPath,
          dirs: new Map(),
          files: [],
        });
      }
      cur = cur.dirs.get(segment)!;
    }
    cur.files.push(fileNode);
  }

  const topFolders = [...hidden.dirs.values()].sort((a, b) =>
    a.segment.localeCompare(b.segment, undefined, { sensitivity: 'base' }),
  );
  const topFiles = [...hidden.files].sort((a, b) =>
    a.segment.localeCompare(b.segment, undefined, { sensitivity: 'base' }),
  );

  return [...topFolders.map(mutableToFolderNode), ...topFiles];
}

/** Parent folder label for display (suite / folder name). */
export function scriptSuiteLabel(filePath: string): string {
  const segs = segmentsUnderScriptsRoot(filePath);
  if (segs.length < 2) return 'Scripts';
  return segs[segs.length - 2] ?? 'Scripts';
}

/** Infer test framework from file extension. */
export function inferScriptFramework(fileName: string): string {
  const name = String(fileName ?? '').toLowerCase();
  if (/\.groovy$/i.test(name)) return 'Katalon';
  if (/\.(spec|test)\.(ts|tsx|js|jsx)$/i.test(name)) return 'Playwright';
  if (/\.py$/i.test(name)) return 'pytest';
  if (/\.java$/i.test(name)) return 'JUnit';
  if (/\.(ts|tsx|js|jsx)$/i.test(name)) return 'JavaScript';
  return 'Script';
}

export function formatScriptIndexedAt(
  iso: string | null | undefined,
): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}

/** Flat file list from inventory rows (deduped, sorted). */
export function listScriptFilesFromRows(
  files: TestScriptFileRow[],
): ScriptFileNode[] {
  const tree = buildScriptFilesTree(files);
  const out: ScriptFileNode[] = [];

  const walk = (nodes: ScriptTreeNode[]) => {
    for (const node of nodes) {
      if (node.type === 'file') out.push(node);
      else walk(node.children);
    }
  };

  walk(tree);
  return out.sort((a, b) =>
    a.filePath.localeCompare(b.filePath, undefined, { sensitivity: 'base' }),
  );
}

/** @deprecated Use buildScriptFilesTree — folders-only hid the real script files. */
export function buildScriptFoldersOnlyTree(
  files: TestScriptFileRow[],
): ScriptFolderNode[] {
  return buildScriptFilesTree(files).filter(
    (n): n is ScriptFolderNode => n.type === 'folder',
  );
}

/** Default: expand folders that contain children. */
export function collectExpandableFolderKeys(
  roots: ScriptTreeNode[],
): Set<string> {
  const keys = new Set<string>();
  const walk = (nodes: ScriptTreeNode[]) => {
    for (const n of nodes) {
      if (n.type !== 'folder') continue;
      if (n.children.length > 0) {
        keys.add(n.pathKey);
        walk(n.children);
      }
    }
  };
  walk(roots);
  return keys;
}
